import { useQueries } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'usehooks-ts';

import type { IStore } from '@boss/services';
import type { DeliveryMethod, StockStatusLine, StockStatusResult } from '@boss/services/client';
import type { IArticle } from '@boss/types/b2b-b2c';

import { stockStatusQuery, useStockStatus } from '../../client-queries/stock';
import type { ArticleIdsObject } from '../../components/ArticlesTable';

export interface PdpStockLineStatus {
  status: 'available' | 'insufficient' | 'unavailable' | null;
  label: string | null;
  isLoading: boolean;
}

export interface UsePdpStockArgs {
  locale: string;
  articles: IArticle[];
  selectedArticleIdsObject: ArticleIdsObject;
  favouriteDelivery?: DeliveryMethod | null;
  favouriteShopId?: string | null;
  stores: IStore[];
}

export interface UsePdpStockResult {
  levermethode: DeliveryMethod;
  setLevermethode: (method: DeliveryMethod) => void;
  afhaalpuntId: string | null;
  setAfhaalpuntId: (id: string | null) => void;
  favouriteShop: IStore | null;
  statusByArticleId: Record<string, PdpStockLineStatus>;
  isPresenceLoading: boolean;
}

/** Typing a quantity fires per keystroke; wait for the value to settle before rechecking the line. */
const QUANTITY_DEBOUNCE_MS = 300;

interface PdpStockActiveLine {
  articleId: string;
  skuid: string;
  convertedQuantity: number;
  convertedUnit: string;
  /** Unit factor missing: the check went out with the line's own unit; a failed check then renders unavailable for this line only. */
  isDefensive: boolean;
}

interface PdpStockResolvedStatus {
  status: PdpStockLineStatus['status'];
  label: string | null;
}

interface PdpStockLineQueryState {
  data?: StockStatusResult;
  isLoading: boolean;
}

interface PdpStockLineCheck {
  line: PdpStockActiveLine;
  query: PdpStockLineQueryState;
}

interface PdpStockFavouriteSelection {
  levermethode: DeliveryMethod;
  afhaalpuntId: string | null;
}

const findStore = (stores: IStore[], id: string | null | undefined) =>
  id ? stores.find(store => store.id === id) ?? null : null;

/** Cases 1–3: Delivery fallback, or Pickup with the favourite shop when that shop is a known store. */
const getFavouriteSelection = (
  favouriteDelivery: DeliveryMethod | null | undefined,
  favouriteShop: IStore | null,
): PdpStockFavouriteSelection => ({
  levermethode: favouriteDelivery === 'Pickup' && favouriteShop ? 'Pickup' : 'Delivery',
  afhaalpuntId: favouriteShop?.id ?? null,
});

/** The favourite shop is prefilled unless the customer explicitly picked a shop themselves. */
const shouldPrefillFavouriteShop = (hasExplicitShopChoice: boolean, favouriteShop: IStore | null) =>
  !hasExplicitShopChoice && !!favouriteShop;

/** Delivery resolves to the main warehouse in the stock service; pickup needs a valid shop. */
const getShopWarehouseCode = (levermethode: DeliveryMethod, selectedStore: IStore | null) =>
  levermethode === 'Pickup' ? selectedStore?.coloradepotid ?? null : null;

const hasStockContext = (levermethode: DeliveryMethod, shopWarehouseCode: string | null) =>
  levermethode === 'Delivery' || !!shopWarehouseCode;

const canFetchPresence = (hasWarehouseContext: boolean, skuCount: number) => hasWarehouseContext && skuCount > 0;

/** Unit factor converting a packaging unit into the article's lowest/sales unit. */
const getUnitFactor = (article: IArticle, unit: string): number | undefined =>
  unit === article.salesUnit
    ? 1
    : article.packaging?.units?.find(packagingUnit => packagingUnit.unit === unit)?.quantity;

/** One article's check args, converted to its lowest/sales unit; undefined when the quantity is 0. */
const getActiveLine = (
  article: IArticle,
  selectedArticleIdsObject: ArticleIdsObject,
): PdpStockActiveLine | undefined => {
  const quantity = selectedArticleIdsObject[article.id]?.quantity ?? 0;

  if (quantity <= 0) {
    return undefined;
  }

  const unit = selectedArticleIdsObject[article.id]?.unit || article.salesUnit;
  const factor = getUnitFactor(article, unit);

  return {
    articleId: article.id,
    skuid: article.stockproductnumber,
    convertedQuantity: factor ? quantity * factor : quantity,
    convertedUnit: factor ? article.salesUnit : unit,
    isDefensive: !factor,
  };
};

/** Lines with quantity > 0, converted to the article's lowest/sales unit. */
const getActiveLines = (articles: IArticle[], selectedArticleIdsObject: ArticleIdsObject): PdpStockActiveLine[] =>
  articles.flatMap(article => {
    const line = getActiveLine(article, selectedArticleIdsObject);

    return line ? [line] : [];
  });

/** A user edit is still inside the debounce window when the debounced line args no longer match the current ones. */
const isDebouncePending = (debouncedLine: PdpStockActiveLine, currentLine: PdpStockActiveLine | undefined) =>
  !currentLine ||
  currentLine.skuid !== debouncedLine.skuid ||
  currentLine.convertedQuantity !== debouncedLine.convertedQuantity ||
  currentLine.convertedUnit !== debouncedLine.convertedUnit;

const getLineStatus = (
  { isDefensive }: PdpStockActiveLine,
  resultLine: StockStatusLine | undefined,
  unavailableLabel: string,
): PdpStockResolvedStatus => {
  if (isDefensive && !resultLine?.status) {
    return { status: 'unavailable', label: unavailableLabel };
  }

  return { status: resultLine?.status ?? null, label: resultLine?.label ?? null };
};

const getPresenceStatus = (
  presenceLines: StockStatusLine[] | undefined,
  isPresenceLoading: boolean,
  skuid: string,
): PdpStockLineStatus => {
  const presenceLine = presenceLines?.find(line => line.skuid === skuid);

  return { status: presenceLine?.status ?? null, label: presenceLine?.label ?? null, isLoading: isPresenceLoading };
};

/**
 * Presence status source. A disabled, never-fetched query reports `isLoading: true` (status 'loading',
 * fetchStatus 'idle'), so it must never drive the presence status: without a warehouse context (pickup,
 * no afhaalpunt chosen yet) qty-0 lines show "no status" instead of an endless skeleton.
 * `isInitialLoading` keeps the initial fetch of enabled queries loading.
 */
const getPresenceState = (
  hasWarehouseContext: boolean,
  data: StockStatusResult | undefined,
  isInitialLoading: boolean,
): { presenceLines: StockStatusLine[] | undefined; isPresenceLoading: boolean } => ({
  presenceLines: hasWarehouseContext ? data?.lines : undefined,
  isPresenceLoading: hasWarehouseContext && isInitialLoading,
});

/** The debounced line + query for one article, when the debounce has produced a check for it. */
const getDebouncedCheck = (
  articleId: string,
  debouncedActiveLines: PdpStockActiveLine[],
  lineQueries: PdpStockLineQueryState[],
): PdpStockLineCheck | undefined => {
  const lineIndex = debouncedActiveLines.findIndex(line => line.articleId === articleId);

  if (lineIndex === -1) {
    return undefined;
  }

  const line = debouncedActiveLines[lineIndex];
  const query = lineQueries[lineIndex];

  return line && query ? { line, query } : undefined;
};

/** One launched line check: its result, or the previous/presence status while it is still pending. */
const resolveLineCheck = (
  articleId: string,
  { line, query }: PdpStockLineCheck,
  currentLine: PdpStockActiveLine | undefined,
  previousStatuses: Record<string, PdpStockResolvedStatus>,
  presenceStatus: PdpStockLineStatus,
  unavailableLabel: string,
): PdpStockLineStatus => {
  if (isDebouncePending(line, currentLine) || query.isLoading) {
    return { ...(previousStatuses[articleId] ?? presenceStatus), isLoading: true };
  }

  const resolved = getLineStatus(line, query.data?.lines?.[0], unavailableLabel);

  previousStatuses[articleId] = resolved;

  return { ...resolved, isLoading: false };
};

interface ResolveArticleStatusArgs {
  articleId: string;
  quantity: number;
  hasWarehouseContext: boolean;
  presenceStatus: PdpStockLineStatus;
  activeLines: PdpStockActiveLine[];
  debouncedActiveLines: PdpStockActiveLine[];
  lineQueries: PdpStockLineQueryState[];
  previousStatuses: Record<string, PdpStockResolvedStatus>;
  unavailableLabel: string;
}

/**
 * Effective status for one article line:
 * qty 0 → presence; qty > 0 → its own per-line check (previous/presence status while loading);
 * no warehouse context (pickup without shop) → no status at all.
 */
const resolveArticleStatus = ({
  articleId,
  quantity,
  hasWarehouseContext,
  presenceStatus,
  activeLines,
  debouncedActiveLines,
  lineQueries,
  previousStatuses,
  unavailableLabel,
}: ResolveArticleStatusArgs): PdpStockLineStatus => {
  if (quantity <= 0) {
    return presenceStatus;
  }

  if (!hasWarehouseContext) {
    return { status: null, label: null, isLoading: false };
  }

  const check = getDebouncedCheck(articleId, debouncedActiveLines, lineQueries);

  if (!check) {
    return { ...(previousStatuses[articleId] ?? presenceStatus), isLoading: true };
  }

  const currentLine = activeLines.find(line => line.articleId === articleId);

  return resolveLineCheck(articleId, check, currentLine, previousStatuses, presenceStatus, unavailableLabel);
};

interface BuildStatusByArticleIdArgs {
  articles: IArticle[];
  selectedArticleIdsObject: ArticleIdsObject;
  presenceLines: StockStatusLine[] | undefined;
  isPresenceLoading: boolean;
  hasWarehouseContext: boolean;
  activeLines: PdpStockActiveLine[];
  debouncedActiveLines: PdpStockActiveLine[];
  lineQueries: PdpStockLineQueryState[];
  previousStatuses: Record<string, PdpStockResolvedStatus>;
  unavailableLabel: string;
}

const buildStatusByArticleId = ({
  articles,
  selectedArticleIdsObject,
  presenceLines,
  isPresenceLoading,
  hasWarehouseContext,
  activeLines,
  debouncedActiveLines,
  lineQueries,
  previousStatuses,
  unavailableLabel,
}: BuildStatusByArticleIdArgs): Record<string, PdpStockLineStatus> =>
  articles.reduce<Record<string, PdpStockLineStatus>>((statuses, article) => {
    statuses[article.id] = resolveArticleStatus({
      articleId: article.id,
      quantity: selectedArticleIdsObject[article.id]?.quantity ?? 0,
      hasWarehouseContext,
      presenceStatus: getPresenceStatus(presenceLines, isPresenceLoading, article.stockproductnumber),
      activeLines,
      debouncedActiveLines,
      lineQueries,
      previousStatuses,
      unavailableLabel,
    });

    return statuses;
  }, {});

/**
 * Page-scoped stock orchestration for the PDP: levermethode/afhaalpunt selection with
 * favourite defaults, a presence check per skuid, and one debounced quantity check per
 * active line (converted to the article's lowest unit). Nothing is persisted.
 */
export const usePdpStock = ({
  locale,
  articles,
  selectedArticleIdsObject,
  favouriteDelivery,
  favouriteShopId,
  stores,
}: UsePdpStockArgs): UsePdpStockResult => {
  const { t } = useTranslation('product');

  const translations = useMemo(
    () => ({
      available: t('productPanel.stock.availability.available'),
      insufficient: t('productPanel.stock.availability.insufficient'),
      unavailable: t('productPanel.stock.availability.unavailable'),
    }),
    [t],
  );

  const favouriteShop = findStore(stores, favouriteShopId);
  const [levermethode, _setLevermethode] = useState<DeliveryMethod>(
    () => getFavouriteSelection(favouriteDelivery, favouriteShop).levermethode,
  );
  const [afhaalpuntId, _setAfhaalpuntId] = useState<string | null>(
    () => getFavouriteSelection(favouriteDelivery, favouriteShop).afhaalpuntId,
  );

  // An explicit in-page choice always wins over account preferences that only arrive later;
  // choices are never persisted (page-scoped). Tracked separately per control so choosing a
  // levermethode does not suppress the later favourite-shop prefill (or vice versa).
  const hasExplicitLevermethodeRef = useRef(false);
  const hasExplicitAfhaalpuntRef = useRef(false);

  const setLevermethode = useCallback((method: DeliveryMethod) => {
    hasExplicitLevermethodeRef.current = true;
    _setLevermethode(method);
  }, []);

  const setAfhaalpuntId = useCallback((id: string | null) => {
    hasExplicitAfhaalpuntRef.current = true;
    _setAfhaalpuntId(id);
  }, []);

  // Favourite defaults run while account data loads/arrives, but never over the matching explicit choice.
  useEffect(() => {
    const selection = getFavouriteSelection(favouriteDelivery, favouriteShop);

    if (!hasExplicitLevermethodeRef.current) {
      _setLevermethode(selection.levermethode);
    }

    if (shouldPrefillFavouriteShop(hasExplicitAfhaalpuntRef.current, favouriteShop)) {
      _setAfhaalpuntId(selection.afhaalpuntId);
    }
  }, [favouriteDelivery, favouriteShop]);

  const selectedStore = findStore(stores, afhaalpuntId);
  const shopWarehouseCode = getShopWarehouseCode(levermethode, selectedStore);
  const hasWarehouseContext = hasStockContext(levermethode, shopWarehouseCode);

  const presenceSkus = useMemo(() => articles.map(article => ({ skuid: article.stockproductnumber })), [articles]);

  const presenceQuery = useStockStatus(
    { skus: presenceSkus, deliveryMethod: levermethode, shopWarehouseCode, locale, translations },
    { enabled: canFetchPresence(hasWarehouseContext, presenceSkus.length) },
  );

  const { presenceLines, isPresenceLoading } = getPresenceState(
    hasWarehouseContext,
    presenceQuery.data,
    presenceQuery.isInitialLoading,
  );

  const activeLines = useMemo(
    () => getActiveLines(articles, selectedArticleIdsObject),
    [articles, selectedArticleIdsObject],
  );
  const debouncedActiveLines = useDebounce(activeLines, QUANTITY_DEBOUNCE_MS);

  const lineQueries = useQueries({
    queries: debouncedActiveLines.map(line =>
      stockStatusQuery(
        {
          skus: [{ skuid: line.skuid, quantity: line.convertedQuantity, unit: line.convertedUnit }],
          deliveryMethod: levermethode,
          shopWarehouseCode,
          locale,
          translations,
        },
        hasWarehouseContext,
      ),
    ),
  });

  // Last completed per-line result, kept during debounce/refetch so statuses never flash empty.
  const previousStatusRef = useRef<Record<string, PdpStockResolvedStatus>>({});

  const statusByArticleId = buildStatusByArticleId({
    articles,
    selectedArticleIdsObject,
    presenceLines,
    isPresenceLoading,
    hasWarehouseContext,
    activeLines,
    debouncedActiveLines,
    lineQueries,
    previousStatuses: previousStatusRef.current,
    unavailableLabel: translations.unavailable,
  });

  return {
    levermethode,
    setLevermethode,
    afhaalpuntId,
    setAfhaalpuntId,
    favouriteShop,
    statusByArticleId,
    isPresenceLoading,
  };
};
